import { useState, useEffect, useRef } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import { ClipboardList, CheckCircle2, XCircle, Trash2, AlertTriangle, RefreshCw, Plus, Search, X, Hourglass, FolderOpen, MapPin, Rocket, Zap, Sparkles, Star, Layers } from "lucide-react";
// ─── AP DESIGN TOKENS (matching ProjectList.js exactly) ──────────────────────
const T = {
  violet: "#5C2D91",
  violetMid: "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red: "#C8102E",
  redLight: "#FCEAED",
  orange: "#E8640A",
  orangeLight: "#FEF0E6",
  gold: "#C87800",
  goldLight: "#FEF5DC",
  green: "#1A7A42",
  greenLight: "#E2F5EC",
  bg: "#F4F0FA",
  surface: "#FFFFFF",
  surfaceAlt: "#FAF8FD",
  dark: "#1A0A2E",
  muted: "#6B5A8A",
  faint: "#C4B8DC",
  border: "rgba(92,45,145,0.1)",
  borderSolid: "#E8E0F5",
};

// ─── Phase colour config — UNCHANGED logic ────────────────────────────────────
const phaseColors = {
  "phase 1": { color: T.violet, bg: T.violetLight, border: T.borderSolid, icon: MapPin },
  "phase 2": { color: T.green, bg: T.greenLight, border: "#B0E8C8", icon: Rocket },
  "phase 3": { color: T.gold, bg: T.goldLight, border: "#F0D888", icon: Zap },
  "phase 4": { color: T.red, bg: T.redLight, border: "#F8C8D0", icon: Sparkles },
  "phase 5": { color: T.violetMid, bg: T.violetLight, border: T.borderSolid, icon: Star },
};

const getPhaseCfg = (phaseName) => {
  const p = (phaseName || "").toLowerCase();
  for (const key of Object.keys(phaseColors)) {
    if (p.includes(key)) return phaseColors[key];
  }
  return { color: T.violetMid, bg: T.violetLight, border: T.borderSolid, icon: Layers };
};

// ─── Row number avatar ────────────────────────────────────────────────────────
const ROW_GRADIENTS = [
  `linear-gradient(135deg,${T.violet},${T.violetMid})`,
  `linear-gradient(135deg,${T.green},#22A458)`,
  `linear-gradient(135deg,${T.gold},#E69A0B)`,
  `linear-gradient(135deg,${T.red},#E02E49)`,
  `linear-gradient(135deg,${T.violetMid},#9B6FD0)`,
  `linear-gradient(135deg,${T.orange},#F07830)`,
];

function RowAvatar({ index }) {
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 9,
      background: ROW_GRADIENTS[index % ROW_GRADIENTS.length],
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 800, fontSize: 13, color: "#fff",
      flexShrink: 0, userSelect: "none",
      boxShadow: "0 2px 6px rgba(92,45,145,0.25)",
    }}>
      {index + 1}
    </div>
  );
}
function Modal({ modal, onClose }) {
  if (!modal) return null;
  const isConfirm = modal.type === "confirm";
  const isError = modal.type === "error";
  const iconMap = { success: <CheckCircle2 size={22} />, error: <XCircle size={22} />, confirm: <Trash2 size={22} />, validation: <AlertTriangle size={22} /> };
  const colorMap = {
    success: { accent: T.green, light: T.greenLight, border: "#A7F3D0" },
    error: { accent: T.red, light: T.redLight, border: "#FECDD3" },
    confirm: { accent: T.red, light: T.redLight, border: "#FECDD3" },
    validation: { accent: T.orange, light: T.orangeLight, border: "#FED7AA" },
  };
  const c = colorMap[modal.type] || colorMap.success;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(26,10,46,0.45)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div style={{
        background: T.surface, borderRadius: 20, width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(92,45,145,0.22)",
        border: `1px solid ${T.borderSolid}`, overflow: "hidden",
      }}>
        <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${c.accent})` }} />
        <div style={{ padding: "28px 28px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: c.light, border: `1.5px solid ${c.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>{iconMap[modal.type]}</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: T.dark }}>{modal.title}</div>
          </div>
          <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.65, margin: "0 0 24px" }}>
            {modal.message}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            {isConfirm && (
              <button onClick={onClose} style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 600,
                background: T.surfaceAlt, color: T.muted,
                border: `1.5px solid ${T.borderSolid}`, cursor: "pointer",
              }}>Cancel</button>
            )}
            <button
              onClick={() => { if (modal.onConfirm) modal.onConfirm(); onClose(); }}
              style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 700,
                background: isConfirm || isError
                  ? `linear-gradient(135deg,${T.red},#E02040)`
                  : `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none", cursor: "pointer",
              }}
            >{isConfirm ? "Delete" : "OK"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function TaskMaster() {


  // ── state — ALL UNCHANGED ──
  const [tasks, setTasks] = useState([]);
  const [phaseList, setPhaseList] = useState([]);
  const [form, setForm] = useState({ phase: "", title: "" });
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [hov, setHov] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const formRef = useRef(null)
  const [refreshing, setRefreshing] = useState(false);

  // ── NEW: confirmDelete state — only addition ──────────────────────────────
  // null = modal closed. A task id = modal open for that task.
  // const [confirmDelete, setConfirmDelete] = useState(null);
  const [modal, setModal] = useState(null);
  const showModal = (type, title, message, onConfirm = null) =>
    setModal({ type, title, message, onConfirm });
  const closeModal = () => setModal(null);

  // ── fetch — UNCHANGED ──
  const fetchDashboardDependencies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [tasksRes, phasesRes] = await Promise.all([
        API.get("/admin/tasks", config),
        API.get("/admin/phases", config),
      ]);
      if (tasksRes.data.success) {
        setTasks(tasksRes.data.tasks || []);
      }
      if (phasesRes.data.success) {
        const mappedPhases = (phasesRes.data.data || []).map((p) => p.name || p.title);
        setPhaseList(mappedPhases);
      }
    } catch (err) {
      console.error("Failed synchronizing task/phase datasets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboardDependencies(); }, []);

  // ── save — UNCHANGED ──
  const handleSave = async () => {
    const taskTitle = form.title.trim();

    // Phase Required
    if (!form.phase) {
      showModal("validation", "Required Field", "Please select Phase.");
      return;
    }

    // Task Required
    if (!taskTitle) {
      showModal("validation", "Required Field", "Task title is required.");
      return;
    }

    // Only letters, numbers & spaces
    if (!/^[A-Za-z0-9 ]+$/.test(taskTitle)) {
      showModal("validation", "Validation Error", "Task title can contain only letters, numbers and spaces.");
      return;
    }

    // Minimum Length
    if (taskTitle.length < 3) {
      showModal("validation", "Validation Error", "Task title must be at least 3 characters.");
      return;
    }

    // Maximum Length
    if (taskTitle.length > 100) {
      showModal("validation", "Validation Error", "Task title cannot exceed 100 characters.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const computedCode = "TSK-" + Math.floor(100 + Math.random() * 900);
      const payload = editingId
        ? { title: taskTitle, phase: form.phase, status: "Active" }           // no code on update
        : { title: taskTitle, phase: form.phase, code: computedCode, status: "Active" }; // code only on create
      if (editingId) {
        const res = await API.put(`/admin/tasks/${editingId}`, payload, config);
        if (res.data.success) showModal("success", "Task Updated", "Task updated successfully.");
      } else {
        const res = await API.post("/admin/tasks", payload, config);
        if (res.data.success) showModal("success", "Task Created", "New task added successfully.");
      }
      setForm({ phase: "", title: "" }); setEditingId(null); setShowForm(false);
      fetchDashboardDependencies();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to finalize task operation.";
      const isDuplicate = msg.toLowerCase().includes("already exists");
      showModal(
        isDuplicate ? "validation" : "error",
        isDuplicate ? "Duplicate Task" : "Save Failed",
        msg
      );
    }
  };

  // ── edit / reset — UNCHANGED ──
  const handleEdit = (task) => {
    setForm({ phase: task.phase || task.assigned_role || "", title: task.title || task.name || "" });
    setEditingId(task.id);
    setShowForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };
  // ── delete — ONLY CHANGE: window.confirm replaced by setConfirmDelete ──────
  const handleDelete = (id) => {
    showModal(
      "confirm",
      "Delete Task",
      "Are you sure you want to delete this task? This action cannot be undone.",
      async () => {
        try {
          const token = localStorage.getItem("token");
          const config = { headers: { Authorization: `Bearer ${token}` } };
          const res = await API.delete(`/admin/tasks/${id}`, config);
          if (res.data.success) {
            showModal("success", "Task Deleted", "Task removed successfully.");
            fetchDashboardDependencies();
          }
        } catch (err) {
          showModal("error", "Delete Failed", err.response?.data?.message || "Failed to remove task registry.");
        }
      }
    );
  };

  // ── execDelete — exact same API logic that was inside the old confirm ──────
  // const execDelete = async () => {
  //   const id = confirmDelete;
  //   setConfirmDelete(null); // close modal first
  //   try {
  //     const token  = localStorage.getItem("token");
  //     const config = { headers: { Authorization: `Bearer ${token}` } };
  //     const res    = await API.delete(`/admin/tasks/${id}`, config);
  //   if (res.data.success) { showModal("success", "Task Deleted", "Task removed successfully."); fetchDashboardDependencies(); }
  //   } catch (err) {
  //   showModal("error", "Delete Failed", err.response?.data?.message || "Failed to remove task registry.");
  //   }
  // };

  const handleResetForm = () => { setForm({ phase: "", title: "" }); setEditingId(null); setShowForm(false); };

  // ── derived — UNCHANGED ──
  const filteredTasks = tasks.filter((task) => {
    const matchTitle = (task.title || task.name || "").toLowerCase();
    const matchPhase = (task.phase || task.assigned_role || "").toLowerCase();
    const query = search.toLowerCase();
    return matchTitle.includes(query) || matchPhase.includes(query);
  });
  const activeCount = tasks.filter((t) => t.status === "Active").length;

  // ── pagination ──
  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / PER_PAGE));
  const paged = filteredTasks.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <AdminLayout>
      <Modal modal={modal} onClose={closeModal} />
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
       .tm-root * { font-family: 'Montserrat', sans-serif !important; }
        .tm-row:hover { background: ${T.violetFaint} !important; }
        .tm-search:focus-within {
          border-color: ${T.violet} !important;
          box-shadow: 0 0 0 3px rgba(92,45,145,0.1) !important;
        }
        .tm-add-btn:hover {
          box-shadow: 0 6px 20px rgba(92,45,145,0.45) !important;
          transform: translateY(-1px);
        }
        .tm-add-btn { transition: all 0.18s ease !important; }
.tm-refresh:hover { opacity: 0.85; }
        .tm-refresh { transition: opacity 0.15s; cursor: pointer; }
        @keyframes tm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        
        /* ── Delete modal animation ── */
        @keyframes tm-modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
        .tm-modal-card { animation: tm-modal-in 0.18s ease both; }

        @media (max-width: 1024px) { .tm-kpi-strip { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width: 768px) {
          .tm-header     { flex-direction: column !important; gap: 14px !important; }
          .tm-kpi-strip  { grid-template-columns: repeat(2,1fr) !important; }
          .tm-filter-row { flex-direction: column !important; align-items: flex-start !important; }
          .tm-table-wrap { font-size: 12px !important; }
        }
        @media (max-width: 480px) {
          .tm-kpi-strip { grid-template-columns: 1fr 1fr !important; }
          .tm-page-pad  { padding: 14px 12px !important; }
        }
      `}</style>

      {/* ══════════════════════════════════════════════════════════════════════
          DELETE CONFIRMATION MODAL — replaces window.confirm
      ══════════════════════════════════════════════════════════════════════ */}


      <div className="tm-root tm-page-pad" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ── PAGE HEADER ── */}
        <div className="tm-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.violet},${T.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · Masters
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
                Task Master
              </h1>
              <span style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", fontSize: 9, fontWeight: 700,
                padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                textTransform: "uppercase", boxShadow: `0 2px 8px ${T.violet}40`,
              }}>ADMIN</span>
            </div>
            <p style={{ color: T.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              Manage project tasks linked to phase pipeline stages
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              className="tm-refresh"
              onClick={async () => {
                setRefreshing(true);
                const minSpin = new Promise(resolve => setTimeout(resolve, 700));
                await Promise.all([fetchDashboardDependencies(), minSpin]);
                setRefreshing(false);
              }}
              disabled={refreshing}
              style={{
                width: 40, height: 40, borderRadius: 11,
                border: `1.5px solid ${T.borderSolid}`,
                background: T.surface, color: T.muted,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0,
                cursor: refreshing ? "not-allowed" : "pointer",
                opacity: refreshing ? 0.7 : 1,
              }}
              title="Refresh"
            >
              <span style={{
                display: "inline-flex",
                animation: refreshing ? "tm-spin 0.7s linear infinite" : "none",
              }}><RefreshCw size={16} /></span>
            </button>

            <button
              className="tm-add-btn"
              onClick={() => { if (showForm) { handleResetForm(); } else { setShowForm(true); } }}
              style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none",
                padding: "11px 22px", borderRadius: 12,
                fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: `0 4px 14px ${T.violet}40`,
                whiteSpace: "nowrap", cursor: "pointer",
              }}
            >
              <Plus size={16} />
              {editingId ? "Edit Task" : showForm ? "Close Form" : "Add Task"}
            </button>
          </div>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="tm-kpi-strip" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 24 }}>
          {[
            { label: "Total Tasks", value: loading ? "…" : tasks.length, icon: <ClipboardList size={22} />, color: T.violet, bg: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: "#D8C8F5" },
            { label: "Active Tasks", value: loading ? "…" : activeCount, icon: <CheckCircle2 size={22} />, color: T.green, bg: `linear-gradient(135deg,${T.greenLight},#EAFAF0)`, border: "#B0E8C8" },
            { label: "Total Phases", value: loading ? "…" : phaseList.length, icon: <Hourglass size={22} />, color: T.gold, bg: `linear-gradient(135deg,${T.goldLight},#FFF8E0)`, border: "#F0D888" },
          ].map((s) => (
            <div key={s.label} style={{
              background: T.surface, borderRadius: 16, border: `1px solid ${T.borderSolid}`,
              padding: "20px 22px", display: "flex", alignItems: "center", gap: 14,
              boxShadow: "0 2px 12px rgba(92,45,145,0.06)", position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", right: -10, top: -10, width: 70, height: 70, borderRadius: "50%", background: s.bg, opacity: 0.6 }} />
              <div style={{ width: 46, height: 46, borderRadius: 13, background: s.bg, border: `1.5px solid ${s.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, position: "relative" }}>{s.icon}</div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                <div style={{ fontWeight: 800, fontSize: 28, color: s.color, lineHeight: 1 }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── FORM CARD ── */}
        {showForm && (
          <div ref={formRef} style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.borderSolid}`, overflow: "hidden", marginBottom: 24, boxShadow: "0 4px 24px rgba(92,45,145,0.08)", scrollMarginTop: 100 }}>
            <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />
            <div style={{ padding: "26px 28px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.violetLight, display: "flex", alignItems: "center", justifyContent: "center" }}><ClipboardList size={17} /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>{editingId ? "Edit Task Details" : "Create New Task"}</div>
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>Fill in the details below</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 20, marginBottom: 22 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>Project Phase</label>
                  <select value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })}
                    style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${T.borderSolid}`, padding: "11px 14px", fontSize: 14, color: T.dark, background: T.surfaceAlt, outline: "none", boxSizing: "border-box", height: "44px", transition: "border-color 0.2s" }}
                    onFocus={(e) => e.target.style.borderColor = T.violet}
                    onBlur={(e) => e.target.style.borderColor = T.borderSolid}
                  >
                    <option value="">Select Phase</option>
                    {phaseList.map((phase) => (<option key={phase} value={phase}>{phase}</option>))}
                  </select>
                  {form.phase && (() => {
                    const cfg = getPhaseCfg(form.phase);
                    return (
                      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 11, color: T.muted }}>Selected:</span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: "2px 10px", borderRadius: 20, border: `1px solid ${cfg.border}` }}>
                          <cfg.icon size={12} /> {form.phase}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>Task Title</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Install base coat paint"
                    style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${T.borderSolid}`, padding: "11px 14px", fontSize: 14, color: T.dark, background: T.surfaceAlt, outline: "none", boxSizing: "border-box", height: "44px", transition: "border-color 0.2s" }}
                    onFocus={(e) => e.target.style.borderColor = T.violet}
                    onBlur={(e) => e.target.style.borderColor = T.borderSolid}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleSave}
                  style={{ background: `linear-gradient(135deg,${T.violet},${T.violetMid})`, color: "#fff", border: "none", padding: "11px 28px", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: `0 4px 14px ${T.violet}40`, transition: "all 0.2s" }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >{editingId ? "Update Task" : "Save Task"}</button>
                <button onClick={handleResetForm}
                  style={{ background: T.surfaceAlt, color: T.muted, border: `1.5px solid ${T.borderSolid}`, padding: "11px 20px", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                >Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── MAIN TABLE CARD ── */}
        <div style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.borderSolid}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(92,45,145,0.08)" }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />
          <div style={{ padding: "20px 24px" }}>

            {/* Toolbar */}
            <div className="tm-filter-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>All Project Tasks</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 400 }}>
                  {filteredTasks.length} of {tasks.length} tasks registered
                  {search && <span style={{ color: T.violet, fontWeight: 600 }}> · filtered</span>}
                </div>
              </div>
              <div className="tm-search" style={{ display: "flex", alignItems: "center", gap: 8, background: T.surfaceAlt, border: `1.5px solid ${T.borderSolid}`, borderRadius: 12, padding: "9px 14px", minWidth: 260, transition: "all 0.2s" }}>
                <span style={{ color: T.faint, display: "inline-flex" }}><Search size={15} /></span>
                <input type="text" placeholder="Search phase or task title..." value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  style={{ border: "none", background: "transparent", fontSize: 13, color: T.dark, outline: "none", width: "100%", fontWeight: 400 }}
                />
                {search && (
                  <button onClick={() => { setSearch(""); setPage(1); }} style={{ background: T.violetLight, border: "none", color: T.violet, cursor: "pointer", width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><X size={11} /></button>
                )}
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div style={{ textAlign: "center", padding: "64px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <div style={{ width: 56, height: 56, borderRadius: 16, background: T.violetLight, display: "flex", alignItems: "center", justifyContent: "center" }}><ClipboardList size={24} color={T.violet} /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: T.dark, marginBottom: 4 }}>Loading tasks</div>
                  <div style={{ fontSize: 13, color: T.muted }}>Synchronising task and phase datasets…</div>
                </div>
              </div>
            ) : (
              <div className="tm-table-wrap" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 520 }}>
                  <thead>
                    <tr style={{ background: T.violetFaint }}>
                      {["#", "Phase", "Task Title", "Actions"].map((h, i) => (
                        <th key={h} style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.09em", padding: "12px 16px", textAlign: i === 0 ? "center" : "left", borderBottom: `2px solid ${T.borderSolid}`, borderTop: `1px solid ${T.borderSolid}`, whiteSpace: "nowrap", background: T.violetFaint }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ padding: "52px", textAlign: "center" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                            <div style={{ width: 64, height: 64, borderRadius: 18, background: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: `2px dashed ${T.faint}`, display: "flex", alignItems: "center", justifyContent: "center" }}><FolderOpen size={28} color={T.violet} /></div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: T.dark }}>No tasks found</div>
                            <div style={{ fontSize: 13, color: T.muted, maxWidth: 300 }}>
                              {search ? `No tasks match "${search}". Try a different search term.` : "No tasks have been created yet. Click Add Task to get started."}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : paged.map((task, i) => {
                      const globalIndex = (page - 1) * PER_PAGE + i;
                      const phaseName = task.phase || task.assigned_role || "";
                      const cfg = getPhaseCfg(phaseName);
                      const isLast = i === paged.length - 1;
                      return (
                        <tr key={task.id} className="tm-row"
                          onMouseEnter={() => setHov(task.id)}
                          onMouseLeave={() => setHov(null)}
                          style={{ background: T.surface, transition: "background 0.12s" }}
                        >
                          <td style={{ padding: "15px 16px", textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 26, height: 26, borderRadius: 8,
                              background: T.violetFaint,
                              fontWeight: 700, fontSize: 11, color: T.muted,
                            }}>{globalIndex + 1}</span>
                          </td>
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: "5px 12px", borderRadius: 20, border: `1px solid ${cfg.border}`, whiteSpace: "nowrap" }}>
                              {phaseName}
                            </span>
                          </td>
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: T.dark }}>{task.title || task.name}</div>
                          </td>
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={() => handleEdit(task)}
                                style={{ background: T.violetLight, color: T.violet, border: `1px solid ${T.borderSolid}`, padding: "7px 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap" }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.violet; e.currentTarget.style.color = "#fff"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = T.violetLight; e.currentTarget.style.color = T.violet; }}
                              ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg></button>

                              {/* Delete button — now opens modal instead of window.confirm */}
                              <button onClick={() => handleDelete(task.id)}
                                style={{ background: T.redLight, color: T.red, border: "1px solid #F8C8D0", padding: "7px 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap" }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.red; e.currentTarget.style.color = "#fff"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = T.redLight; e.currentTarget.style.color = T.red; }}
                              ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredTasks.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.borderSolid}`, flexWrap: "wrap", gap: 12 }}>
                <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
                  Showing <strong style={{ color: T.dark }}>{Math.min((page - 1) * PER_PAGE + 1, filteredTasks.length)}–{Math.min(page * PER_PAGE, filteredTasks.length)}</strong> of <strong style={{ color: T.dark }}>{filteredTasks.length}</strong> tasks
                </span>
                {totalPages > 1 && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                      style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${page === 1 ? T.borderSolid : T.violet}`, background: page === 1 ? T.surface : T.violetLight, color: page === 1 ? T.faint : T.violet, cursor: page === 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, transition: "all 0.15s" }}>‹ Prev</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, page - 3), Math.min(totalPages, page + 2)).map(n => (
                      <button key={n} onClick={() => setPage(n)}
                        style={{ width: 34, height: 34, borderRadius: 10, border: `1.5px solid ${n === page ? T.violet : T.borderSolid}`, background: n === page ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface, color: n === page ? "#fff" : T.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: n === page ? `0 2px 8px ${T.violet}40` : "none", transition: "all 0.15s" }}>{n}</button>
                    ))}
                    <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                      style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${page === totalPages ? T.borderSolid : T.violet}`, background: page === totalPages ? T.surface : T.violetLight, color: page === totalPages ? T.faint : T.violet, cursor: page === totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, transition: "all 0.15s" }}>Next ›</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}